import { useRef, useMemo, useState } from 'react';
import type {
  GetSolutionMapCommandOutput,
  IndustryMeta,
  SolutionMapEntry,
} from '../../../client/IvtHubClient';
import { priorityTier } from '../../../utils/priority';
import { SPAN_AREA } from '../utils/spanCoverage';
import PriorityLegend from './PriorityLegend';
import SolutionMapCell from './SolutionMapCell';
import SpanRow from './SpanRow';

export interface IndustryMatrixHandlers {
  onCyclePriority?: (industry: string, entry: SolutionMapEntry) => void;
  onMoveUc?: (industry: string, entry: SolutionMapEntry, direction: 'UP' | 'DOWN') => void;
  onRenameUc?: (industry: string, entry: SolutionMapEntry, newName: string) => void;
  onSoftDeleteUc?: (industry: string, entry: SolutionMapEntry) => void;
  onAddUc?: (industry: string, solutionArea: string) => void;
  onMoveArea?: (industry: string, area: string, direction: 'LEFT' | 'RIGHT') => void;
  onRenameArea?: (industry: string, area: string, ucCount: number) => void;
  onDeleteArea?: (industry: string, area: string, ucCount: number) => void;
  onAddArea?: (industry: string) => void;
  onAddSpanUc?: (industry: string) => void;
  onUpdateSpanCoverage?: (industry: string, entry: SolutionMapEntry, coveredAreas: string[]) => void;
}

interface IndustryMatrixProps extends IndustryMatrixHandlers {
  meta: IndustryMeta;
  data?: GetSolutionMapCommandOutput;
  columnHeaderColor: string;
  editMode?: boolean;
  locked?: boolean;
  onToggleEdit?: (industry: string) => void;
  searchQuery?: string;
}

function AreaHeader({
  area,
  areaIdx,
  totalAreas,
  editMode,
  locked,
  columnHeaderColor,
  ucCount,
  industry,
  onMoveArea,
  onRenameArea,
  onDeleteArea,
}: {
  area: string;
  areaIdx: number;
  totalAreas: number;
  editMode: boolean;
  locked: boolean;
  columnHeaderColor: string;
  ucCount: number;
  industry: string;
  onMoveArea?: IndustryMatrixHandlers['onMoveArea'];
  onRenameArea?: IndustryMatrixHandlers['onRenameArea'];
  onDeleteArea?: IndustryMatrixHandlers['onDeleteArea'];
}) {
  const headerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [offsetX, setOffsetX] = useState(0);
  const offsetXRef = useRef(0);
  const dragStartX = useRef(0);

  const isFirst = areaIdx === 0;
  const isLast = areaIdx === totalAreas - 1;
  const canMoveLeft = !isFirst;
  const canMoveRight = !isLast;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!editMode || locked) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragStartX.current = e.clientX;
    offsetXRef.current = 0;

    const colWidth = headerRef.current?.getBoundingClientRect().width ?? 156;
    const maxLeft = canMoveLeft ? -colWidth : 0;
    const maxRight = canMoveRight ? colWidth : 0;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - dragStartX.current;
      const clamped = Math.max(maxLeft, Math.min(maxRight, dx));
      offsetXRef.current = clamped;
      setOffsetX(clamped);
      setDragging(true);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setDragging(false);
      setOffsetX(0);

      const currentOffset = offsetXRef.current;
      const threshold = (headerRef.current?.getBoundingClientRect().width ?? 156) * 0.4;
      if (currentOffset < -threshold && canMoveLeft) {
        onMoveArea?.(industry, area, 'LEFT');
      } else if (currentOffset > threshold && canMoveRight) {
        onMoveArea?.(industry, area, 'RIGHT');
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      ref={headerRef}
      className={
        'ivt-industry-matrix__column-header' +
        (editMode ? ' ivt-industry-matrix__column-header--editing' : '') +
        (dragging ? ' ivt-industry-matrix__column-header--dragging' : '')
      }
      style={{
        background: editMode ? 'rgba(37, 37, 37, 0.7)' : columnHeaderColor,
        transform: dragging ? `translateX(${offsetX}px)` : undefined,
        zIndex: dragging ? 10 : undefined,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={() => {
        if (editMode && !locked) onRenameArea?.(industry, area, ucCount);
      }}
      onContextMenu={(e) => {
        if (editMode && !locked) {
          e.preventDefault();
          onDeleteArea?.(industry, area, ucCount);
        }
      }}
      title={editMode ? 'Drag to reorder; double-click to rename; right-click to delete' : undefined}
    >
      <span className="ivt-industry-matrix__column-name">{area}</span>
    </div>
  );
}

export default function IndustryMatrix({
  meta,
  data,
  columnHeaderColor,
  editMode = false,
  locked = false,
  onToggleEdit,
  searchQuery,
  onCyclePriority,
  onMoveUc,
  onRenameUc,
  onSoftDeleteUc,
  onAddUc,
  onMoveArea,
  onRenameArea,
  onDeleteArea,
  onAddArea,
  onAddSpanUc,
  onUpdateSpanCoverage,
}: IndustryMatrixProps) {
  const industry = meta.industry ?? '';

  const { groups, spans, areaNames, totalUCs, counts } = useMemo(() => {
    const active = (data?.entries ?? []).filter((e) => e.status !== 'DEPRECATED');
    const g = new Map<string, SolutionMapEntry[]>();
    const s: SolutionMapEntry[] = [];
    let strategic = 0;
    let recommended = 0;
    let opportunistic = 0;

    active.forEach((e) => {
      const tier = priorityTier(e.priority);
      if (tier === 'strategic') strategic++;
      else if (tier === 'recommended') recommended++;
      else opportunistic++;

      if (e.solutionArea === SPAN_AREA) {
        s.push(e);
        return;
      }
      const key = e.solutionArea ?? '';
      const list = g.get(key);
      if (list) list.push(e);
      else g.set(key, [e]);
    });
    const names = [...g.keys()];
    return {
      groups: g,
      spans: s,
      areaNames: names,
      totalUCs: active.length,
      counts: { strategic, recommended, opportunistic },
    };
  }, [data]);

  const maxRows = areaNames.length
    ? Math.max(...[...groups.values()].map((c) => c.length))
    : 0;

  const matchesSearch = (entry: SolutionMapEntry) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (entry.useCase ?? '').toLowerCase().includes(q) ||
      (entry.solutionArea ?? '').toLowerCase().includes(q)
    );
  };

  return (
    <div className="ivt-industry-matrix__card">
      <div className="ivt-industry-matrix__header">
        <span className="ivt-industry-matrix__name">{industry} Solution Map 2026</span>
        {!editMode && onToggleEdit && (
          <button
            type="button"
            className="ivt-industry-matrix__outline-btn"
            onClick={() => onToggleEdit(industry)}
          >
            Edit
          </button>
        )}
        {editMode && (
          <>
            <button
              type="button"
              className="ivt-industry-matrix__outline-btn"
              disabled={locked}
              onClick={() => onAddArea?.(industry)}
            >
              加 Solution Area
            </button>
            <button
              type="button"
              className="ivt-industry-matrix__save-btn"
              disabled={locked}
              onClick={() => onToggleEdit?.(industry)}
            >
              Done
            </button>
          </>
        )}
      </div>

      {!editMode && <PriorityLegend counts={counts} total={totalUCs} />}

      {areaNames.length === 0 ? (
        <div className="ivt-industry-matrix__empty">No Solution Map entries for this industry.</div>
      ) : (
        <div className="ivt-industry-matrix__grid">
          {areaNames.map((area, areaIdx) => {
            const entries = groups.get(area) ?? [];
            return (
              <div key={area} className="ivt-industry-matrix__column">
                <AreaHeader
                  area={area}
                  areaIdx={areaIdx}
                  totalAreas={areaNames.length}
                  editMode={editMode}
                  locked={locked}
                  columnHeaderColor={columnHeaderColor}
                  ucCount={entries.length}
                  industry={industry}
                  onMoveArea={onMoveArea}
                  onRenameArea={onRenameArea}
                  onDeleteArea={onDeleteArea}
                />
                {entries.map((e, idx) => (
                  <SolutionMapCell
                    key={e.id}
                    entry={e}
                    editMode={editMode}
                    locked={locked}
                    isFirst={idx === 0}
                    isLast={idx === entries.length - 1}
                    highlighted={!!searchQuery && matchesSearch(e)}
                    dimmed={!!searchQuery && !matchesSearch(e)}
                    onCyclePriority={() => onCyclePriority?.(industry, e)}
                    onMove={(target, dir) => onMoveUc?.(industry, target, dir)}
                    onRename={(_e, name) => onRenameUc?.(industry, e, name)}
                    onSoftDelete={() => onSoftDeleteUc?.(industry, e)}
                  />
                ))}
                {!editMode &&
                  Array.from({ length: Math.max(0, maxRows - entries.length) }).map((_, i) => (
                    <div key={`pad-${i}`} className="ivt-industry-matrix__pad" />
                  ))}
                {editMode && (
                  <button
                    type="button"
                    className="ivt-industry-matrix__add-uc"
                    disabled={locked}
                    onClick={() => onAddUc?.(industry, area)}
                  >
                    + UC
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(spans.length > 0 || editMode) && areaNames.length > 0 && (
        <div className="ivt-industry-matrix__spans">
          {spans.map((e) => (
            <SpanRow
              key={e.id}
              entry={e}
              areaNames={areaNames}
              editMode={editMode}
              locked={locked}
              highlighted={!!searchQuery && matchesSearch(e)}
              dimmed={!!searchQuery && !matchesSearch(e)}
              onCyclePriority={() => onCyclePriority?.(industry, e)}
              onRename={(_e, name) => onRenameUc?.(industry, e, name)}
              onSoftDelete={() => onSoftDeleteUc?.(industry, e)}
              onUpdateCoverage={(_entry, covered) => onUpdateSpanCoverage?.(industry, e, covered)}
            />
          ))}
          {editMode && (
            <button
              type="button"
              className="ivt-industry-matrix__add-span"
              disabled={locked}
              onClick={() => onAddSpanUc?.(industry)}
            >
              + Span Row
            </button>
          )}
        </div>
      )}
    </div>
  );
}
